import { extractOdomPosition } from "../types/ar-types.js";
//#region ../src/ar/depth-sampler.ts
/**
* Defensively copy a 16-element column-major matrix into a plain serializable
* tuple. Returns undefined for missing input, the wrong length, or any
* non-finite entry — so an invalid (or UA-reused/garbage) source degrades to
* "absent" rather than poisoning downstream maths. The copy also de-aliases
* Float32Arrays the UA reuses across frames.
*/
function copyValidMatrix16(src) {
	if (!src || src.length !== 16) return;
	const copy = Array.from(src);
	return copy.every((v) => Number.isFinite(v)) ? copy : void 0;
}
/**
* Wrap a raw browser XRDepthInformation object into a {@link DepthInfo}.
*
* - `getDepthInMeters` is bound to the source object (browser
*   implementations are this-sensitive).
* - `projectionMatrix` (typically `XRView.projectionMatrix`, a Float32Array
*   the UA may reuse across frames) is defensively validated and copied into
*   a plain serializable 16-tuple; invalid input (wrong length, non-finite
*   entries) yields a DepthInfo without a matrix rather than an error.
* - The live-occluder metadata (`data`, `rawValueToMeters`,
*   `normDepthBufferFromNormView`) is preserved when present and valid: `data`
*   by live reference (no clone — see {@link DepthInfo.data}), the scale only
*   when finite, and the UV transform's `.matrix` copied like
*   `projectionMatrix`. The sparse grid sampler ignores all three; a source
*   lacking them (e.g. the existing test doubles) wraps exactly as before.
*/
function wrapXRDepthInfo(raw, projectionMatrix) {
	const wrapped = {
		width: raw.width,
		height: raw.height,
		getDepthInMeters: raw.getDepthInMeters.bind(raw)
	};
	const projection = copyValidMatrix16(projectionMatrix);
	if (projection) wrapped.projectionMatrix = projection;
	if (raw.data instanceof ArrayBuffer) wrapped.data = raw.data;
	if (typeof raw.rawValueToMeters === "number" && Number.isFinite(raw.rawValueToMeters)) wrapped.rawValueToMeters = raw.rawValueToMeters;
	const uvTransform = copyValidMatrix16(raw.normDepthBufferFromNormView?.matrix);
	if (uvTransform) wrapped.normDepthBufferFromNormView = uvTransform;
	return wrapped;
}
/**
* Recommended depth-sampling cadence for apps that RECONSTRUCT-and-render an
* occupancy mesh (the Recorder, the PhysicsDemo) — the depth-side counterpart
* of `DEFAULT_OCCUPANCY_CELL_SIZE_M`/`DEFAULT_OCCUPANCY_MIN_OBSERVATIONS`
* (occupancy-grid.ts): one framework-level source of truth so both apps build
* the mesh at the same speed (2026-07-16 field feedback: the demo relied on
* the conservative fallback below — 16×16 @ 1 Hz, 8× fewer points/s than the
* recorder — and reconstructed visibly slower).
*
* Tuning (2026-07-16 EVENING, maintainer's on-device framerate/mesh trade-off
* passes — supersedes the same-day sweep-derived 500 ms × 64): 200 ms × 24×24.
* The ground-truth density/cadence sweep
* (GpsPlusSlamJs_Investigation test-results/synthetic-density-cadence-sweep.txt)
* showed density is the more point-efficient mesh-speed lever, but its
* flagged open question — on-device frame-time cost — resolved against LARGE
* per-sample batches: 64² @ 2 Hz (8192 points/s) visibly hurt the framerate,
* while many SMALL samples (24² @ 5 Hz, ~2880 points/s) keep the per-frame
* work chunk tiny and rendering smooth at good mesh build-up. If devices get
* faster, the sweep says gridSize (not the interval) is the knob to raise
* first.
*
* Deliberately OPT-IN named constants, NOT a change to the fallback below:
* bumping the fallback would silently re-tune consumers that are not
* reconstruction apps (MinimalExample / AnchorStarter).
*/
const DEFAULT_RECONSTRUCTION_DEPTH_INTERVAL_MS = 200;
const DEFAULT_RECONSTRUCTION_DEPTH_GRID_SIZE = 24;
const DEFAULT_CONFIG = {
	intervalMs: 1e3,
	gridSize: 16,
	unavailabilityThresholdMs: 5e3,
	rgb: true
};
/**
* Samples sparse depth points from WebXR depth sensing.
*
* Usage:
* ```ts
* const sampler = new DepthSampler({
*   onSampleCaptured: (sample) => saveSample(sample),
*   getCurrentPose: () => arSession.getCurrentPose(),
* });
* sampler.start();
* // In frame loop:
* sampler.onFrame(timestamp, depthInfo);
* ```
*/
var DepthSampler = class {
	callbacks;
	config;
	running = false;
	sampleCount = 0;
	lastSampleTime = -Infinity;
	/** Timestamp when sampling started (for unavailability detection) */
	startTime = -Infinity;
	/** Whether we've ever received valid depth data */
	depthReceived = false;
	/** Whether we've already fired the unavailable callback */
	unavailableCallbackFired = false;
	constructor(callbacks, config) {
		this.callbacks = callbacks;
		this.config = { ...DEFAULT_CONFIG };
		if (config) this.updateConfig(config);
	}
	/**
	* Start depth sampling.
	*/
	start() {
		this.running = true;
		this.sampleCount = 0;
		this.lastSampleTime = -Infinity;
		this.startTime = performance.now();
		this.depthReceived = false;
		this.unavailableCallbackFired = false;
	}
	/**
	* Stop depth sampling.
	*/
	stop() {
		this.running = false;
	}
	/**
	* Check if sampler is currently running.
	*/
	isRunning() {
		return this.running;
	}
	/**
	* Get the number of samples captured since start.
	*/
	getSampleCount() {
		return this.sampleCount;
	}
	/**
	* Get the current configuration.
	*/
	getConfig() {
		return { ...this.config };
	}
	/**
	* Apply partial configuration overrides (e.g. the user's recording
	* options, plumbed in by `startDepthCapture` just before sampling
	* starts). Invalid values are ignored defensively: every key requires a
	* finite positive number, and `gridSize` additionally an integer.
	*/
	updateConfig(config) {
		if (isFinitePositive(config.intervalMs)) this.config.intervalMs = config.intervalMs;
		if (isFinitePositive(config.gridSize) && Number.isInteger(config.gridSize)) this.config.gridSize = config.gridSize;
		if (isFinitePositive(config.unavailabilityThresholdMs)) this.config.unavailabilityThresholdMs = config.unavailabilityThresholdMs;
		if (typeof config.rgb === "boolean") this.config.rgb = config.rgb;
	}
	/**
	* Called each frame with a LAZY depth provider (quality-review E-4: the
	* caller used to acquire + wrap the depth info every frame while this
	* method threw ~59 of 60 acquisitions away at the interval check — the
	* provider is now invoked only when a sample is due).
	*
	* Unavailability detection is preserved by construction:
	* `lastSampleTime` only advances when a sample is EMITTED, so while depth
	* is unavailable the sampler stays "due" and probes the provider every
	* frame — exactly the cadence the old per-frame acquisition gave
	* `checkDepthUnavailability` (Field Test Readiness Issue #8).
	*
	* @param timestamp - Current frame timestamp in milliseconds
	* @param acquireDepthInfo - Returns the frame's depth info, or null if
	*   unavailable. Only invoked when a sample is due.
	*/
	onFrame(timestamp, acquireDepthInfo) {
		if (!this.running) return;
		if (timestamp - this.lastSampleTime < this.config.intervalMs) return;
		const depthInfo = acquireDepthInfo();
		if (!depthInfo) {
			this.checkDepthUnavailability();
			return;
		}
		this.depthReceived = true;
		const pose = this.callbacks.getCurrentPose();
		if (!pose) return;
		const points = this.sampleGrid(depthInfo, this.acquireRgbLookupSafely());
		const sample = {
			timestamp: performance.timeOrigin + timestamp,
			cameraPos: extractOdomPosition(pose),
			cameraRot: [
				pose.orientation.x,
				pose.orientation.y,
				pose.orientation.z,
				pose.orientation.w
			],
			points,
			...depthInfo.projectionMatrix ? { projectionMatrix: depthInfo.projectionMatrix } : {}
		};
		this.lastSampleTime = timestamp;
		this.sampleCount++;
		this.callbacks.onSampleCaptured(sample);
	}
	/**
	* Acquire the per-sample RGB lookup, gated by `config.rgb` and guarded so
	* a failing acquisition (e.g. GL context loss during the blit) can never
	* break the sample emission in the XR frame loop.
	*/
	acquireRgbLookupSafely() {
		if (!this.config.rgb || !this.callbacks.acquireRgbLookup) return null;
		try {
			return this.callbacks.acquireRgbLookup();
		} catch {
			return null;
		}
	}
	/**
	* Sample a grid of depth points from the depth buffer, attaching the
	* camera color per point when a lookup is available.
	*/
	sampleGrid(depthInfo, rgbLookup) {
		const points = [];
		const gridSize = this.config.gridSize;
		for (let row = 0; row < gridSize; row++) for (let col = 0; col < gridSize; col++) {
			const screenX = (col + 1) / (gridSize + 1);
			const screenY = (row + 1) / (gridSize + 1);
			const depthM = depthInfo.getDepthInMeters(screenX, screenY);
			const rgb = rgbLookup?.(screenX, screenY);
			points.push({
				screenX,
				screenY,
				depthM,
				...rgb ? { rgb } : {}
			});
		}
		return points;
	}
	/**
	* Check if depth has been unavailable for longer than the threshold.
	* If so, fire the onDepthUnavailable callback (once).
	*/
	checkDepthUnavailability() {
		if (this.depthReceived || this.unavailableCallbackFired) return;
		if (!this.callbacks.onDepthUnavailable) return;
		if (performance.now() - this.startTime >= this.config.unavailabilityThresholdMs) {
			this.unavailableCallbackFired = true;
			this.callbacks.onDepthUnavailable();
		}
	}
	/**
	* Check if depth data has ever been received.
	* Useful for testing and status display.
	*/
	hasReceivedDepth() {
		return this.depthReceived;
	}
};
function isFinitePositive(value) {
	return typeof value === "number" && Number.isFinite(value) && value > 0;
}
//#endregion
export { DEFAULT_RECONSTRUCTION_DEPTH_GRID_SIZE, DEFAULT_RECONSTRUCTION_DEPTH_INTERVAL_MS, DepthSampler, wrapXRDepthInfo };
